import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMediaPipe } from "../../../../../../hooks/useMediaPipe";
import { DEVICE_CHECKS, FACE_TIMEOUT_MS, HOLD_DURATION_MS, RECORDING_SECONDS } from "../constants";
import type { Check, UpdateCheck } from "../constants";
import { mediaErrorMessage } from "../systemChecks";
import { useAudioRecordingCheck } from "../hooks/useAudioRecordingCheck";
import CameraView from "./CameraView";
import AudioView from "./AudioView";
import ResultView from "./ResultView";

/**
 * Runs the camera, face scan, microphone and audio recording checks one
 * after another, moving to the next when the current one passes or fails.
 * The audio checks wait for the candidate to click Next.
 * Mounted only once the candidate starts the test, so permissions are
 * requested and the face detection model is loaded on demand.
 * Nothing captured here is uploaded.
 */
const DeviceTest: React.FC<{
  update: UpdateCheck;
  failedCount: number;
  onDone: () => void;
}> = ({ update, failedCount, onDone }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { modelReady, modelError, faceStatus } = useMediaPipe(videoRef);
  // Key of the check currently running; undefined once all are done
  const [stepIndex, setStepIndex] = useState(0);
  const step: string | undefined = DEVICE_CHECKS[stepIndex]?.key;
  const [camStream, setCamStream] = useState<MediaStream | null>(null);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  // Set when the candidate clicks Next after the camera checks
  const [audioStarted, setAudioStarted] = useState(false);
  // Snapshot taken when the face scan passes
  const [faceCapture, setFaceCapture] = useState<string | null>(null);
  const streamsRef = useRef<MediaStream[]>([]);

  /** Record the result of a check and move on to the next one */
  const finish = useCallback(
    (key: string, check: Check) => {
      update(key, check);
      setStepIndex((prev) => prev + 1);
    },
    [update],
  );

  // Release camera and microphone on unmount
  useEffect(() => {
    const streams = streamsRef.current;
    return () => {
      streams.forEach((s) => s.getTracks().forEach((t) => t.stop()));
      streams.length = 0;
    };
  }, []);

  // ── 1. Camera ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== "camera") return;
    let cancelled = false;
    update("camera", { status: "running", detail: "Waiting for permission…" });

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamsRef.current.push(stream);
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCamStream(stream);
        finish("camera", {
          status: "pass",
          detail: stream.getVideoTracks()[0]?.label || "Camera is working",
        });
      } catch (err) {
        if (!cancelled) finish("camera", { status: "fail", detail: mediaErrorMessage(err, "Camera") });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step, update, finish]);

  // ── 2. Face scan ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== "face") return;
    if (!camStream) {
      finish("face", { status: "fail", detail: "Camera access is needed for the face scan" });
      return;
    }
    if (modelError) {
      finish("face", { status: "fail", detail: `Could not load face detection: ${modelError}` });
      return;
    }
    if (!modelReady) {
      update("face", { status: "running", detail: "Loading face detection model…" });
      return;
    }
    if (faceStatus.count === 0) {
      update("face", {
        status: "running",
        detail: "No face detected. Look straight at the camera in a well-lit place.",
      });
      return;
    }
    if (faceStatus.count > 1) {
      update("face", {
        status: "running",
        detail: `${faceStatus.count} faces detected. Only one person should be in the frame.`,
      });
      return;
    }

    update("face", { status: "running", detail: "Face detected — hold still…" });
    const timeoutId = setTimeout(() => {
      const video = videoRef.current;
      if (video?.videoWidth) {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d")?.drawImage(video, 0, 0);
        setFaceCapture(canvas.toDataURL("image/jpeg", 0.9));
      }
      finish("face", { status: "pass", detail: "Your face was detected successfully" });
    }, HOLD_DURATION_MS);
    return () => clearTimeout(timeoutId);
  }, [step, camStream, modelReady, modelError, faceStatus.count, update, finish]);

  // Give up on the face scan if no single face is found in time
  useEffect(() => {
    if (step !== "face" || !camStream || !modelReady) return;
    const timeoutId = setTimeout(() => {
      finish("face", {
        status: "fail",
        detail: "Could not detect a single face. Improve your lighting, face the camera and run the test again.",
      });
    }, FACE_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [step, camStream, modelReady, finish]);

  // ── 3. Microphone ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== "microphone") return;
    if (!audioStarted) {
      update("microphone", { status: "pending", detail: "Click Next to test your audio" });
      return;
    }
    let cancelled = false;
    update("microphone", { status: "running", detail: "Waiting for permission…" });

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamsRef.current.push(stream);
        setMicStream(stream);
        finish("microphone", {
          status: "pass",
          detail: stream.getAudioTracks()[0]?.label || "Microphone is working",
        });
      } catch (err) {
        if (!cancelled) {
          finish("microphone", { status: "fail", detail: mediaErrorMessage(err, "Microphone") });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step, audioStarted, update, finish]);

  // ── 4. Audio recording ────────────────────────────────────────────────────
  const { micLevel, secondsLeft, playbackUrl } = useAudioRecordingCheck(
    step === "recording",
    micStream,
    update,
    finish,
  );

  // The camera and audio tests are separate screens: camera off once audio starts
  const startAudio = () => {
    camStream?.getTracks().forEach((t) => t.stop());
    setAudioStarted(true);
  };

  const done = step === undefined;

  useEffect(() => {
    if (done) onDone();
  }, [done, onDone]);

  return (
    <div className="space-y-4">
      {/* Kept mounted (hidden) after the camera checks for the face detection hook */}
      <CameraView
        videoRef={videoRef}
        hidden={audioStarted}
        hasCamera={!!camStream}
        waitingForPermission={step === "camera"}
        modelReady={modelReady}
        faceStatus={faceStatus}
      />

      {audioStarted && !done && (
        <AudioView
          state={step === "recording" ? "recording" : "waiting"}
          micLevel={micLevel}
          secondsLeft={secondsLeft}
        />
      )}

      {done && (
        <ResultView failedCount={failedCount} faceCapture={faceCapture} playbackUrl={playbackUrl} />
      )}

      {step === "microphone" && !audioStarted && (
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-3">
            Camera checks are done. Next, we'll record {RECORDING_SECONDS} seconds of audio to test
            your microphone.
          </p>
          <button
            onClick={startAudio}
            className="py-2.5 px-8 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors"
          >
            Next
          </button>
        </div>
      )}

    </div>
  );
};

export default DeviceTest;
